import React, { useMemo } from "react";
import "./RecommendationCard.css";
import { RecommendationData } from "ta-agent-sdk";
import { useMaps } from "../../contexts/MapsContext";
import { useTranslation } from "react-i18next";

interface RecommendationCardProps {
  recommendation: RecommendationData;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation,
  ...props
}) => {
  const { t } = useTranslation();
  const { openDetails } = useMaps();
  // Type guards and data extraction
  const isLocation = "location" in recommendation;
  const isExperience = "experience" in recommendation;

  const mainPhoto = useMemo(() => {
    return recommendation.photos &&
      recommendation.photos.photos &&
      recommendation.photos.photos.length > 0
      ? recommendation.photos.photos[0]
      : null;
  }, [recommendation.photos]);

  if (isLocation) {
    const { location } = recommendation;
    const primaryName =
      location.names.find((name) => name.primary === true)?.value ||
      location.names[0]?.value ||
      "";
    const rating =
      location.traveler_ratings?.overall.rating ??
      location.overall_traveller_ratings?.bubble_rating ??
      0;
    const reviewCount =
      location.traveler_ratings?.overall.count ??
      location.overall_traveller_ratings?.total_review_count ??
      0;

    // Expanded card view
    return (
      <div
        className="recommendation-card bg-white rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer w-full md:w-[350px] shrink-0 relative"
        onClick={() => openDetails(recommendation, false)}
        {...props}
      >
        {/* <button
          className={`${
            mainPhoto ? "absolute" : "relative"
          } top-2 left-2 bg-[#00EB5B] text-black font-trip-sans-bold shadow-md text-sm px-2 py-1 rounded z-10`}
          onClick={() => openDetails(recommendation, true)}
        >
          {t("common.findOutMore")}
        </button> */}
        {mainPhoto && (
          <div className="relative h-28 overflow-hidden">
            <img
              src={mainPhoto.StandardSizeURL.url}
              alt={primaryName}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div className="p-4">
          {/* Title */}
          <div className="mb-2">
            <h3 className="text-md md:text-lg font-semibold text-gray-900 line-clamp-1">
              {primaryName}
            </h3>
          </div>

          {/* Rating and Reviews */}
          <div className="flex items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm md:text-base font-trip-sans-medium mr-1 text-gray-600">
                {rating.toFixed(1)}
              </span>
              <div className="flex gap-1">
                {Array.from({ length: 5 }, (_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      i < rating ? "bg-[#284B35]" : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs md:text-sm text-gray-600">
                {t("common.reviews", { count: reviewCount })}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (isExperience) {
    const { experience } = recommendation;
    const primaryName = experience.names[0]?.value || "";
    const rating = experience.overall_traveller_ratings.bubble_rating;
    const reviewCount = experience.overall_traveller_ratings.total_review_count;

    // Expanded card view
    return (
      <div
        className="recommendation-card bg-white rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer w-full md:w-[350px] shrink-0 relative"
        onClick={() => openDetails(recommendation, false)}
        {...props}
      >
        {/* <button
          className={`${
            mainPhoto || experience.photo_url ? "absolute" : "relative"
          } top-2 left-2 bg-[#00EB5B] text-black font-trip-sans-bold shadow-md text-sm px-2 py-1 rounded z-10`}
          onClick={() => openDetails(recommendation, false)}
        >
          {t("common.findOutMore")}
        </button> */}
        {/* Image */}
        {(mainPhoto || experience.photo_url) && (
          <div className="relative h-28 overflow-hidden">
            <img
              src={mainPhoto?.StandardSizeURL.url || experience.photo_url}
              alt={primaryName}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div className="p-4">
          {/* Title */}
          <div className="mb-2">
            <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
              {primaryName}
            </h3>
          </div>

          {/* Rating and Reviews */}
          <div className="flex items-center">
            <div className="flex items-center gap-2">
              <span className="font-trip-sans-medium mr-1 text-gray-600">
                {rating.toFixed(1)}
              </span>
              <div className="flex gap-1">
                {Array.from({ length: 5 }, (_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      i < rating ? "bg-[#284B35]" : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
              <span className="text-gray-600 text-sm">
                {t("common.reviews", { count: reviewCount })}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default RecommendationCard;
